import React, { RefObject, useEffect, useRef, useState } from "react"
import classNames from "classnames"

import { useAppSelector } from "../../../hooks"

import * as ScrollUtils from "../utils/ScrollUtils"
import {
    selectAIModalById,
    selectLastMessage,
    selectLastUserMessage,
    selectSortedMessages,
} from "../selectors"
import { IId } from ".."

import Message from "./messages/Message"
import LoadingIndicatorMessage from "./messages/LoadingIndicatorMessage"

export interface Props extends IId {
    scrollRef: RefObject<HTMLDivElement>
}

export default function Messages({ id, scrollRef }: Props): JSX.Element {
    const AIModalData = useAppSelector((state) => selectAIModalById(state, id))

    if (!AIModalData) {
        return <></>
    }
    const { isLoading } = AIModalData

    const sortedMessages = useAppSelector(() =>
        selectSortedMessages(AIModalData),
    )
    const lastUserMessage = useAppSelector(() =>
        selectLastUserMessage(AIModalData),
    )
    const lastMessage = useAppSelector(() => selectLastMessage(AIModalData))

    const loadingElementRef = useRef<HTMLDivElement>(null)
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    const messageRefs = useRef<Record<string, HTMLDivElement | null>>({})

    const [spacerHeight, setSpacerHeight] = useState<number>(0)

    // for spacer height calculation
    // when scrolled all the way to the bottom
    // the user's last message should be at the top of the modal
    useEffect(() => {
        if (!!lastMessage && !!lastUserMessage) {
            // if the last message is sent by the user
            // then we have to calculate the good height for the spacer
            // relative to the last user message
            if (lastMessage === lastUserMessage) {
                const messageElement = messageRefs.current[lastUserMessage?.id]
                if (!messageElement) {
                    return
                }

                setSpacerHeight(
                    ScrollUtils.getSpacerHeightRelativeToNewUserMessage(
                        scrollRef,
                        messageElement,
                        loadingElementRef,
                        messagesContainerRef,
                    ),
                )
            }
            // if the last message was not by the user
            // then we have to calculate the good height for the spacer
            // relative to the last visible non-user message
            else {
                const messageElement = messageRefs.current[lastMessage.id]
                if (!messageElement) {
                    return
                }

                setSpacerHeight(
                    ScrollUtils.getSpacerHeightRelativeToLastMessage(
                        scrollRef,
                        messageElement,
                        messagesContainerRef,
                    ),
                )
            }
        }
        // if there are no messages, then the spacer shouldn't be visible
        else {
            setSpacerHeight(0)
        }
    }, [lastMessage, lastUserMessage, loadingElementRef])

    useEffect(() => {
        // if the last message is a user message
        // and the spacer is visible
        // then we have to scroll the last user message
        // to the top of the modal
        if (
            !!lastMessage &&
            !!lastUserMessage &&
            lastMessage.id === lastUserMessage.id &&
            spacerHeight > 0
        ) {
            const messageElement = messageRefs.current[lastUserMessage.id]
            if (!!messageElement && lastMessage === lastUserMessage) {
                ScrollUtils.scrollElement(messageElement)
            }
        }
    }, [lastMessage, lastUserMessage, spacerHeight])

    return (
        <div
            className="tw-flex tw-flex-col tw-items-start tw-gap-2 tw-px-4 tw-pb-4 tw-pt-2"
            data-ft-component="messages"
            ref={messagesContainerRef}
        >
            {sortedMessages.map((message) => {
                const containerClasses = classNames("tw-max-w-9/10", {
                    "tw-self-end": message.isSentByUser,
                })

                return (
                    <div
                        key={message.id}
                        ref={(el): void => {
                            messageRefs.current[message.id] = el
                        }}
                        className={containerClasses}
                    >
                        <Message isLoading={isLoading} message={message} />
                    </div>
                )
            })}

            {isLoading && (
                <div className="tw-max-w-9/10" ref={loadingElementRef}>
                    <LoadingIndicatorMessage />
                </div>
            )}

            {spacerHeight > 0 && (
                <div
                    style={{
                        height: spacerHeight,
                    }}
                />
            )}
        </div>
    )
}
